import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navigation = [
  { name: "Home", href: "/" },
  {
    name: "About",
    href: "/about-pixie-clicks",
    children: [
      { name: "About Pixie Clicks", href: "/about-pixie-clicks" },
      { name: "Our Story & Philosophy", href: "/our-story-philosophy" },
    ],
  },
  
  {
    name: "Services",
    href: "/photography-by-pixie-clicks",
    children: [
      { name: "Photography", href: "/photography-by-pixie-clicks" },
      { name: "Cinematography", href: "/cinematography" },
      { name: "Combo Packages", href: "/combo-packages" },
      { name: "Photo Booth", href: "/photobooth" },
    ],
  },
  { name: "Galleries", href: "/galleries" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container-wide flex h-20 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <span className="font-serif text-2xl font-semibold tracking-tight text-foreground">
            Pixie<span className="text-primary">Clicks</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:items-center lg:gap-1">
          {navigation.map((item) =>
            item.children ? (
              <DropdownMenu key={item.name}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
                      item.children.some((child) => isActive(child.href))
                        ? "text-primary"
                        : "text-muted-foreground"
                    }`}
                  >
                    {item.name}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  {item.children.map((child) => (
                    <DropdownMenuItem key={child.name} asChild>
                      <Link
                        to={child.href}
                        className={isActive(child.href) ? "text-primary" : ""}
                      >
                        {child.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
                  isActive(item.href) ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {item.name}
              </Link>
            )
          )}
        </div>

        {/* CTA Button */}
        <div className="hidden lg:block">
          <Button variant="hero" asChild>
            <Link to="/contact">Check Availability</Link>
          </Button>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="lg:hidden rounded-md p-2.5 text-foreground"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="sr-only">Open main menu</span>
          <Menu className="h-6 w-6" />
        </button>
      </nav>
    </header>

    {/* Mobile menu - rendered via portal to document.body */}
    {createPortal(
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9998] bg-foreground/60 backdrop-blur-md lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-[9999] bg-background shadow-elevated lg:hidden overflow-y-auto"
            >
              <div className="flex flex-col min-h-full px-8 py-8">
                {/* Header with logo and close button */}
                <div className="flex items-center justify-between">
                  <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                    <span className="font-serif text-2xl font-semibold tracking-tight text-foreground">
                      Pixie<span className="text-primary">Clicks</span>
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="rounded-full p-3 text-foreground hover:bg-muted transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <X className="h-7 w-7" />
                  </button>
                </div>

                {/* Navigation - centered vertically on mobile */}
                <div className="flex-1 flex flex-col justify-center py-8">
                  <motion.nav 
                    className="space-y-2"
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: {},
                      visible: {
                        transition: {
                          staggerChildren: 0.08,
                          delayChildren: 0.1
                        }
                      }
                    }}
                  >
                    {navigation.map((item, index) =>
                      item.children ? (
                        <motion.div 
                          key={item.name} 
                          className="space-y-2"
                          variants={{
                            hidden: { opacity: 0, y: 20 },
                            visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
                          }}
                        >
                          <span className="block px-4 py-3 text-xl font-semibold text-foreground">
                            {item.name}
                          </span>
                          <div className="pl-6 space-y-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block rounded-lg px-4 py-3 text-lg font-medium transition-colors hover:bg-muted ${
                                  isActive(child.href)
                                    ? "text-primary bg-muted"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key={item.name}
                          variants={{
                            hidden: { opacity: 0, y: 20 },
                            visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
                          }}
                        >
                          <Link
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block rounded-lg px-4 py-3 text-xl font-medium transition-colors hover:bg-muted ${
                              isActive(item.href)
                                ? "text-primary bg-muted"
                                : "text-muted-foreground"
                            }`}
                          >
                            {item.name}
                          </Link>
                        </motion.div>
                      )
                    )}
                  </motion.nav>
                </div>

                {/* CTA Button at bottom */}
                <div className="pt-4">
                  <Button variant="hero" size="lg" className="w-full text-lg py-6" asChild>
                    <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                      Check Availability
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    )}
    </>
  );
}
